import axios from 'axios';
import { DHLError, isEmptyString, axiosRequest, handleHttpResponse } from '@general/js/utils';
import AbstractClass from './BenefitQuotesAbstractServices';
import { Services } from '../Services';

export default class BenefitQuotesServices extends AbstractClass {
  /**
   * Implicitly returns a Promise for fetching list of members, their products and modalities.
   *
   * Note that by default `fetchPathway` is to be `"E"`
   */
  static async getMembersProductsAndModalities(memberNumber, fetchPathway = 'E') {
    try {
      const queryParams = {
        params: {
          memberNumber,
          fetchPathway,
        },
      };
      const response = await axiosRequest().get(Services.MY_CLAIMS.BENEFIT_QUOTES.GET_MEMBERS_PRODUCTS_AND_MODALITIES, queryParams);

      if (handleHttpResponse(response) && response.data.members && response.data.products && response.data.members.length > 0 && response.data.products.length > 0) {
        return response.data; // "Resolves" the Promise
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`); // Rejects the promise
    } catch (error) {
      throw new DHLError(`getMembersProductsAndModalities() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for fetching multi-step form related reference data from Kentico.
   */
  static async getMultiStepFormReferenceData() {
    try {
      const response = await axios.get(Services.MY_CLAIMS.BENEFIT_QUOTES.GET_REFERENCE_DATA_MULTI_STEP_FORM);

      // Only check for the critical items, e.g. tooth number options since it is required to populate an input field.
      if (handleHttpResponse(response) && response.data.toothNumberOptions && response.data.toothNumberOptions.length > 0) {
        return response.data;
      }
      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getMultiStepFormReferenceData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get ref data of get benefit quote landing page
   */
  static async getLandingPageReferenceData() {
    try {
      const response = await axios.get(Services.MY_CLAIMS.BENEFIT_QUOTES.GET_REFERENCE_DATA_LANDING_PAGE);

      if (handleHttpResponse(response) && response.data.benefitQuoteInformationText && response.data.benefitQuoteCalloutText) {
        // return member number string token swapped data
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getLandingPageReferenceData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for fetching list of providers from OHI.
   */
  static async getProvidersForSelectedModality(modalityCode, providerId, lastName, postcode, state) {
    try {
      const queryParams = {
        params: {
          modalityCode,
        },
      };

      if (isEmptyString(providerId)) {
        if (!isEmptyString(lastName)) {
          queryParams.params.lastName = lastName;
        }

        if (!isEmptyString(postcode)) {
          queryParams.params.postcode = postcode;
        }

        queryParams.params.state = state.toUpperCase(); // State is case sensitive so must always be sent in uppercase to avoid a 204 being returned
      } else {
        queryParams.params.providerId = providerId;
      }

      const response = await axiosRequest().get(Services.MY_CLAIMS.BENEFIT_QUOTES.GET_PROVIDERS_FOR_SELECTED_MODALITY, queryParams);

      if (handleHttpResponse(response) && response.data.providers) {
        return response.data;
      }
      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getProvidersForSelectedModality() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for fetching list of services for a given modality code.
   */
  static async getServicesForSelectedModality(modalityCode) {
    try {
      const queryParams = {
        params: {
          modalityCode,
        },
      };

      const response = await axiosRequest().get(Services.MY_CLAIMS.BENEFIT_QUOTES.GET_SERVICES_FOR_SELECTED_MODALITY, queryParams);

      if (handleHttpResponse(response) && response.data.services && response.data.services.length > 0) {
        return response.data;
      }
      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getServicesForSelectedModality() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for submitting the quote to OHI.
   */
  static async submitBenefitQuote(memberNumber, accessingPersonCode, providerId, providerFlexCode, subProviderIdentifier, subProviderIdentifierType, productCode, services) {
    try {
      const body = {
        providerId,
        providerFlexCode,
        services,
        subProviderIdentifier,
        subProviderIdentifierType,
        productCode,
        isCRM: false, // this should not be needed but including it as swagger documentation suggest it should included
      };

      const response = await axiosRequest().post(Services.MY_CLAIMS.BENEFIT_QUOTES.SUBMIT_BENEFIT_QUOTE(memberNumber, accessingPersonCode), body);
      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`submitBenefitQuote() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for fetching details of a given benefit quote.
   */
  static async getBenefitQuoteDetails(benefitQuoteId, memberNumber, accessingPersonCode, productCode) {
    try {
      const response = await axiosRequest().get(Services.MY_CLAIMS.BENEFIT_QUOTES.GET_BENEFIT_QUOTE_DETAILS(memberNumber, accessingPersonCode, benefitQuoteId, productCode));
      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`getBenefitQuoteDetails() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for searching all benefit quotes for a policy.
   */
  static async search(memberNumber) {
    try {
      const queryParams = {
        params: {
          memberNumber,
        },
      };

      const response = await axiosRequest().get(Services.MY_CLAIMS.BENEFIT_QUOTES.SEARCH, queryParams);

      if (handleHttpResponse(response) && response.data.benefitQuoteList) {
        return response.data;
      }
      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`search() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }
}
